import React, { useState, useEffect } from 'react';
import { Alert, Space, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SlotUnavailableViolation } from '../types';

const { Text } = Typography;

/** Delay before showing the "checking" alert to avoid flashing on every datetime change */
const CHECKING_ALERT_DELAY_MS = 400;

interface ConflictAlertsProps {
  slotUnavailable: SlotUnavailableViolation | null;
  isCheckingSlots: boolean;
  truncated: boolean;
  resourceId: string | null | undefined;
  hasCommittedStartTime?: boolean;
  onDismissSlotUnavailable: () => void;
}

export const ConflictAlerts: React.FC<ConflictAlertsProps> = ({
  slotUnavailable,
  isCheckingSlots,
  truncated,
  resourceId,
  hasCommittedStartTime = false,
  onDismissSlotUnavailable
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');
  const [showCheckingAlert, setShowCheckingAlert] = useState(false);

  useEffect(() => {
    if (!isCheckingSlots) {
      setShowCheckingAlert(false);
      return;
    }
    const timer = setTimeout(() => setShowCheckingAlert(true), CHECKING_ALERT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isCheckingSlots]);

  return (
    <>
      {slotUnavailable?.hasViolation && (
        <Alert
          title={t('appointmentComponents.create.conflicts.timeslotBooked')}
          description={
            <Space orientation="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('appointmentComponents.create.conflicts.selectDifferentTimeOrResource')}
              </Text>
            </Space>
          }
          type="error"
          showIcon
          closable
          onClose={onDismissSlotUnavailable}
          style={{ marginBottom: 0 }}
        />
      )}

      {truncated && (
        <Alert
          title={t('appointmentComponents.create.conflicts.timeslotBooked')}
          description={
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('appointmentComponents.create.conflicts.selectDifferentTimeOrResource')}
            </Text>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 0 }}
        />
      )}

      {showCheckingAlert &&
        isCheckingSlots &&
        resourceId &&
        hasCommittedStartTime &&
        !slotUnavailable?.hasViolation && (
          <Alert
            title={t('appointmentComponents.create.conflicts.checkingAvailability')}
            type="info"
            showIcon={false}
            style={{ marginBottom: 0 }}
          />
        )}
    </>
  );
};
